import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
  TestimonialsV5,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";
import { useContent } from "../lib/useContent";
import { isContactHref } from "../lib/cta";
import { useSeo } from "../lib/useSeo";

/* ============================================================
   /about — Sunday Digital · Our Story
   An editorial monograph treatment of the studio. Carries the
   homepage / creative DNA (yellow accent, mask reveals, marquee
   grammars) but quieter and more reflective. Six "tenets" are
   the centrepiece — each treated as a magazine chapter.
============================================================ */

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
  >
    {children}
    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </button>
);

/* ============================================================
   01 · Hero — colossal "Our Story" with layered photos
============================================================ */
const HERO_LEFT_IMG =
  "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1400&q=70";
const HERO_RIGHT_IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=70";

const AboutHero = ({ onPrimary }) => {
  const cms = useContent("about");
  const h = (cms && cms.hero) || {};
  const breadcrumb = h.breadcrumb || "Our Story";
  const eyebrow = h.eyebrow || "About — Est. 2014 · Brooklyn / Remote";
  const subtitle = h.subtitle ||
    "Two teams. Twenty-five years of combined craft. One studio built on cosmetics, fashion, and lifestyle ecommerce — with a stack that runs end-to-end from UI/UX and Shopify development to creative, content, and paid media.";
  const ctaLabel = h.cta_primary_label || h.cta_label || "Get in touch";
  const secondaryLabel = h.cta_secondary_label || "Read the story";
  const secondaryHref = h.cta_secondary_href || "#story";
  const line1 = h.title_line_1 || "Our";
  const line2 = h.title_line_2 || "Story";
  const photoBack = h.photo_back || HERO_LEFT_IMG;
  const photoFront = h.photo_front || HERO_RIGHT_IMG;
  const badgeNumber = h.badge_number == null ? "+" : h.badge_number;
  const badgeCaption = h.badge_caption || "Two · One";
  const liveMarquee = Array.isArray(h.marquee_words) && h.marquee_words.length
    ? h.marquee_words.map((v) => v.title || "").filter(Boolean)
    : [
        "Cosmetics", "Fashion", "Lifestyle", "Shopify", "Brand systems", "Creative",
        "Cosmetics", "Fashion", "Lifestyle", "Shopify", "Brand systems", "Creative",
      ];
  return (
  <section
    id="top"
    data-testid="about-hero"
    className="relative overflow-hidden bg-[#0B0F14]"
  >
    {/* Soft yellow ambient flare top-right */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(900px 600px at 80% 20%, rgba(244,214,78,0.12), transparent 60%)",
      }}
    />
    {/* Subtle grain */}
    <span
      aria-hidden
      className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.07) 1px, transparent 0)",
        backgroundSize: "3px 3px",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-24 md:pb-28">
      {/* Breadcrumb */}
      <div
        data-reveal="eyebrow"
        className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-12 md:mb-16"
      >
        <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E]">{breadcrumb}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-16 items-center">
        {/* LEFT — typography */}
        <div className="lg:col-span-7 relative">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h1
            data-testid="about-hero-title"
            className="font-display mt-7 text-white leading-[0.9] tracking-[-0.04em] font-black text-[3.5rem] sm:text-[5rem] md:text-[7rem] lg:text-[9rem] xl:text-[10.5rem]"
          >
            <span data-reveal="mask-block">
              <span>{line1}</span>
            </span>
            <span data-reveal="mask-block" style={{ "--i": 1 }} className="block italic font-normal text-[#F4D64E]">
              <span>{line2}.</span>
            </span>
          </h1>

          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-9 max-w-[58ch] text-[1.05rem] md:text-[1.2rem] text-neutral-300 leading-[1.6]"
          >
            <span>
              {subtitle}
            </span>
          </p>

          <div
            data-reveal="eyebrow"
            style={{ "--i": 3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <PrimaryCTA testId="about-hero-cta" onClick={onPrimary}>
              {ctaLabel}
            </PrimaryCTA>
            <a
              href={secondaryHref}
              className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-300 hover:text-[#F4D64E] transition-colors"
            >
              {secondaryLabel}
              <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
            </a>
          </div>
        </div>

        {/* RIGHT — photo collage with layered offset */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] max-w-[26rem] mx-auto">
            {/* Back photo — slightly rotated */}
            <div
              data-reveal="image"
              className="absolute -top-6 -left-4 w-[78%] aspect-[3/4] rounded-2xl overflow-hidden border border-white/[0.08] shadow-[0_50px_120px_-30px_rgba(0,0,0,0.85)] rotate-[-4deg]"
            >
              <img
                src={photoBack}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span aria-hidden className="absolute inset-0 bg-gradient-to-tr from-[#0B0F14]/40 to-transparent" />
            </div>
            {/* Front photo — slightly rotated other way, sits on top */}
            <div
              data-reveal="image"
              style={{ "--i": 1 }}
              className="absolute bottom-0 right-0 w-[80%] aspect-[3/4] rounded-2xl overflow-hidden border border-[#F4D64E]/30 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.85)] rotate-[3deg]"
            >
              <img
                src={photoFront}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span aria-hidden className="absolute inset-0 bg-gradient-to-bl from-[#F4D64E]/15 via-transparent to-transparent" />
            </div>
            {/* Connecting badge */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#F4D64E] text-neutral-950 shadow-[0_20px_50px_-10px_rgba(244,214,78,0.6)]">
              <div className="text-center">
                <div className="font-display text-2xl md:text-3xl font-black tracking-[-0.02em] leading-none">
                  {badgeNumber}
                </div>
                <div className="text-[0.55rem] font-bold tracking-[0.2em] uppercase mt-1">
                  {badgeCaption}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom marquee — running statement */}
    <div className="relative border-t border-white/[0.08] bg-[#0B0F14]/65 backdrop-blur-md">      <div className="overflow-hidden">
        <motion.div
          className="flex items-center gap-12 py-4 whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 40, ease: "linear", repeat: Infinity }}
        >
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-12 shrink-0">
              {liveMarquee.map((w, i) => (
                <span key={`${dup}-${i}`} className="flex items-center gap-12">
                  <span className="font-display text-[1.3rem] md:text-[1.7rem] font-black tracking-[-0.015em] text-white uppercase">
                    {w}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E]" />
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   02 · Origin manifesto — full editorial paragraph with
   pull-quote treatment. The hero of the body copy.
============================================================ */
const Origin = () => {
  const cms = useContent("about");
  const o = cms?.origin || {};
  const eyebrow = o.eyebrow || "The origin";
  const headlineMain = o.headline_main || "Two teams.";
  const headlineItalic = o.headline_italic || "One";
  const headlineAccent = o.headline_accent || "studio";
  const sidebarDescription = o.sidebar_description ||
    "How Sunday Digital came to be — and what we're built to ship every working day since.";
  const introPart1 = o.intro_part_1 ||
    "Sunday Digital is the result of two experienced teams coming together —";
  const introItalic = o.intro_italic || "a combined";
  const introAccent = o.intro_accent || "25+ years";
  const introPart2 = o.intro_part_2 ||
    " of craft in web development and digital marketing, sharpened on the cosmetics, fashion, and lifestyle industries.";
  const paragraph1 = o.paragraph_1 ||
    "The merger let us become a true full-service studio — UI/UX, Shopify ecommerce development, SEO, creative, content and email marketing, social and search advertising, social media management, and everything that orbits a brand's growth.";
  const paragraph2 = o.paragraph_2 ||
    "Today we run as one team — collaborators across UX/UI design, web development, creative services, digital marketing, and social media — shipping the work that ends up under each brand's roof.";
  const statValue = o.stat_value || "500";
  const statSuffix = o.stat_suffix == null ? "+" : o.stat_suffix;
  const statLabel = o.stat_label ||
    "Successfully completed tasks since day one — and counting.";
  return (
  <section
    id="story"
    data-testid="origin"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-16 items-start">
        {/* LEFT — section anchor */}
        <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[2.75rem] lg:text-[3.5rem] font-black tracking-[-0.035em] leading-[1.0] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </span>
          </h2>
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-7 text-[0.95rem] md:text-[1rem] text-neutral-500 leading-[1.65] max-w-[40ch]"
          >
            <span>{sidebarDescription}</span>
          </p>
        </div>

        {/* RIGHT — long-form copy */}
        <div className="lg:col-span-8">
          <div
            data-reveal="mask-block"
            className="font-display text-[1.6rem] sm:text-[1.85rem] md:text-[2.1rem] lg:text-[2.4rem] font-extrabold tracking-[-0.025em] leading-[1.18] text-white"
          >
            <span>
              {introPart1}{" "}
              {introItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{introItalic}</span>{" "}
                </>
              ) : null}
              {introAccent ? (
                <>
                  <PillarAccent>{introAccent}</PillarAccent>
                </>
              ) : null}
              {introPart2}
            </span>
          </div>

          <p
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-10 text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7] max-w-[62ch]"
          >
            <span>{paragraph1}</span>
          </p>

          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-8 text-[1.05rem] md:text-[1.15rem] text-neutral-300 leading-[1.7] max-w-[62ch]"
          >
            <span>{paragraph2}</span>
          </p>

          {/* Pull stat row */}
          <div
            data-reveal="eyebrow"
            style={{ "--i": 3 }}
            className="mt-14 flex items-baseline gap-6 border-t border-white/[0.08] pt-8"
          >
            <span className="font-display text-[3.5rem] md:text-[4.5rem] font-black tracking-[-0.04em] leading-[0.92] text-[#F4D64E] tabular-nums">
              {statValue}
              {statSuffix ? (
                <span className="text-[2rem] md:text-[2.5rem]">{statSuffix}</span>
              ) : null}
            </span>
            <span className="text-[0.78rem] md:text-[0.85rem] font-bold tracking-[0.22em] uppercase text-neutral-400 max-w-[28ch]">
              {statLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   03 · Stats ribbon — 25+ / 500+ / 2 / 1
============================================================ */
const ABOUT_STATS = [
  { v: "25", suffix: "+", label: "Years of combined craft" },
  { v: "500", suffix: "+", label: "Tasks completed" },
  { v: "2", suffix: "→1", label: "Teams merged into one studio" },
  { v: "10", suffix: "+", label: "Disciplines under one roof" },
];

const StatsRibbon = () => {
  const cms = useContent("about");
  const liveStats = Array.isArray(cms?.stats_ribbon?.items) && cms.stats_ribbon.items.length
    ? cms.stats_ribbon.items.map((it) => ({
        v: it.title || "",
        suffix: it.suffix || "",
        label: it.body || "",
      }))
    : ABOUT_STATS;
  return (
  <section
    data-testid="about-stats"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <ul className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8">
        {liveStats.map((s, i) => (
          <li
            key={i}
            data-reveal="mask-block"
            style={{ "--i": i }}
            className="flex flex-col gap-3"
          >
            <span className="font-display text-[3rem] sm:text-[3.5rem] lg:text-[4.5rem] font-black tracking-[-0.04em] leading-[0.92] text-white">
              {s.v}
              <span className="text-[#F4D64E]">{s.suffix}</span>
            </span>
            <span className="text-[0.7rem] md:text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-500 max-w-[24ch]">
              {s.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </section>
  );
};

/* ============================================================
   04 · They Trust Us — "The Index" editorial roster
   A single, high-end client display: numbered list of partners
   on the left, sticky preview frame on the right that cross-
   fades imagery + metadata as the user hovers each row.
   Replaces the older marquee + grid pair which duplicated
   the same names twice.
============================================================ */
const PARTNERS = [
  {
    name: "Veronique Gabai",
    discipline: "Fragrance",
    year: "2019",
    note: "Brand world & flagship Shopify Plus build.",
    img: "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=1400&q=70",
  },
  {
    name: "Café Joyeux",
    discipline: "Food & Mission",
    year: "2021",
    note: "Multi-locale storefront with cause-led editorial.",
    img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1400&q=70",
  },
  {
    name: "Madam Glam",
    discipline: "Beauty · Nail",
    year: "2018",
    note: "Performance redesign, +38% conversion.",
    img: "https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=1400&q=70",
  },
  {
    name: "Sclarandis",
    discipline: "Lifestyle",
    year: "2022",
    note: "Identity refresh + content engine.",
    img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=70",
  },
  {
    name: "Ming Yu Wang",
    discipline: "Designer Jewelry",
    year: "2020",
    note: "Editorial commerce for sculptural pieces.",
    img: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1400&q=70",
  },
  {
    name: "Swand",
    discipline: "Lifestyle",
    year: "2023",
    note: "Subscription-first DTC launch.",
    img: "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=1400&q=70",
  },
  {
    name: "Nail Reserve",
    discipline: "Beauty · Salon",
    year: "2024",
    note: "Full digital storefront from kickoff to launch.",
    img: "https://images.unsplash.com/photo-1604902396830-aca29e19b067?auto=format&fit=crop&w=1400&q=70",
  },
  {
    name: "Bow & Arrow",
    discipline: "Fashion",
    year: "2022",
    note: "Lookbook-grade product storytelling.",
    img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1400&q=70",
  },
];

const PartnerRow = ({ partner, idx, total, onHover, isActive }) => (
  <button
    type="button"
    onMouseEnter={() => onHover(idx)}
    onFocus={() => onHover(idx)}
    data-testid={`partner-row-${idx + 1}`}
    className={`group relative w-full text-left flex items-baseline gap-6 sm:gap-10 py-6 md:py-7 border-t border-white/[0.08] transition-colors duration-500 ${
      isActive ? "border-[#F4D64E]/40" : ""
    } ${idx === total - 1 ? "border-b" : ""}`}
  >
    {/* Index number */}
    <span
      className={`text-[0.7rem] font-bold tracking-[0.3em] uppercase tabular-nums w-12 shrink-0 transition-colors duration-500 ${
        isActive ? "text-[#F4D64E]" : "text-neutral-600"
      }`}
    >
      {String(idx + 1).padStart(2, "0")}
    </span>

    {/* Wordmark */}
    <span
      className={`flex-1 font-display font-black tracking-[-0.025em] leading-none uppercase transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        isActive
          ? "text-[#F4D64E] translate-x-2"
          : "text-neutral-300 group-hover:text-white"
      } text-[1.7rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.4rem]`}
    >
      {partner.name}
    </span>

    {/* Discipline + year (right side) */}
    <span className="hidden md:flex items-baseline gap-6 shrink-0 text-[0.72rem] font-bold tracking-[0.22em] uppercase">
      <span
        className={`transition-colors duration-500 ${
          isActive ? "text-neutral-300" : "text-neutral-500"
        }`}
      >
        {partner.discipline}
      </span>
      <span className="w-px h-3 bg-white/[0.15]" />
      <span
        className={`tabular-nums w-12 text-right transition-colors duration-500 ${
          isActive ? "text-[#F4D64E]" : "text-neutral-600"
        }`}
      >
        {partner.year}
      </span>
    </span>

    {/* Active marker — yellow chevron */}
    <ArrowUpRight
      className={`w-5 h-5 shrink-0 transition-all duration-500 ${
        isActive
          ? "text-[#F4D64E] opacity-100 translate-x-0"
          : "text-neutral-700 opacity-0 -translate-x-2"
      }`}
    />
  </button>
);

const TheyTrustUs = () => {
  const [active, setActive] = useState(0);
  const cms = useContent("about");
  const t = cms?.they_trust_us || {};
  const eyebrow = t.eyebrow || "Our valued partners — the index";
  const headlineMain = t.headline_main || "They";
  const headlineItalic = t.headline_italic || "trust";
  const headlineAccent = t.headline_accent || "us";
  const description = t.description ||
    "Brands that ship with us — across cosmetics, fashion, food, and lifestyle. Hover any partner to see the work.";
  const captionLeft = t.caption_left || "Partner";
  const captionRight = t.caption_right || "Discipline · Year";
  const footerRight = t.footer_right || "Selected work · 2018 — 2025";
  const previewCaption = t.preview_caption || "Hover the index — the work follows.";
  const partners = Array.isArray(t.items) && t.items.length
    ? t.items.map((it) => ({
        name: it.title || "",
        discipline: it.discipline || "",
        year: it.year || "",
        note: it.body || "",
        img: it.media || "",
      }))
    : PARTNERS;
  // Clamp active when list length shrinks via CMS reorder/delete
  const safeActive = Math.min(active, Math.max(0, partners.length - 1));

  return (
    <section
      data-testid="trust-us"
      className="relative bg-[#0B0F14] border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Header row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 items-end mb-12 md:mb-16">
          <div className="lg:col-span-7">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2.25rem] md:text-[3.25rem] lg:text-[4rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
            >
              <span>
                {headlineMain}{" "}
                {headlineItalic ? (
                  <>
                    <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                  </>
                ) : null}
                <PillarAccent>{headlineAccent}</PillarAccent>.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.65]"
            >
              <span>{description}</span>
            </p>
          </div>
        </div>

        {/* Index spread — list on the left, sticky preview on the right.
            Note: no `items-start` here — we WANT the right grid cell to
            stretch to match the left list height so the sticky preview
            actually has runway to stick across the entire roster. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12">
          {/* LEFT — partner index */}
          <div className="lg:col-span-7" onMouseLeave={() => setActive(0)}>
            <div className="flex items-baseline justify-between mb-4">
              <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-600">
                {captionLeft}
              </span>
              <span className="hidden md:inline text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-600">
                {captionRight}
              </span>
            </div>
            <ul>
              {partners.map((p, i) => (
                <li key={i}>
                  <PartnerRow
                    partner={p}
                    idx={i}
                    total={partners.length}
                    onHover={setActive}
                    isActive={safeActive === i}
                  />
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between mt-7 text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-600">
              <span>{partners.length} of {partners.length}</span>
              <span>{footerRight}</span>
            </div>
          </div>

          {/* RIGHT — sticky preview */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div
                data-testid="partner-preview"
                className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0F141A]"
              >
                {/* Crossfaded imagery */}
                {partners.map((p, i) => (
                  <img
                    key={i}
                    src={p.img}
                    alt={p.name}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      safeActive === i ? "opacity-100" : "opacity-0"
                    }`}
                  />
                ))}
                {/* Editorial overlay */}
                <span
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/90 via-[#0B0F14]/30 to-[#0B0F14]/30"
                />
                <span
                  aria-hidden
                  className="absolute inset-0 ring-1 ring-inset ring-[#F4D64E]/20"
                />

                {/* Top-left chip — index */}
                <div className="absolute top-5 left-5 inline-flex items-center gap-3 px-3 py-2 rounded-md bg-black/40 backdrop-blur-md border border-white/10 text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
                  Sunday × {String(safeActive + 1).padStart(2, "0")}
                </div>

                {/* Bottom card — synced meta with crossfade */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
                  {partners.map((p, i) => (
                    <div
                      key={i}
                      className={`absolute inset-x-0 bottom-0 p-6 md:p-7 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                        safeActive === i
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-4 pointer-events-none"
                      }`}
                    >
                      <div className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] mb-3">
                        {p.discipline}{p.discipline && p.year ? " · " : ""}{p.year}
                      </div>
                      <div className="font-display text-[1.5rem] md:text-[1.85rem] font-black tracking-[-0.02em] text-white leading-[1.05] mb-2">
                        {p.name}
                      </div>
                      <div className="text-[0.92rem] text-neutral-300 leading-[1.55] max-w-[36ch] italic">
                        {p.note}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Caption beneath preview */}
              <p className="mt-5 text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-600 text-center">
                {previewCaption}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   05 · How We Do It — six tenets as editorial chapters
   Each tenet is a "spread": huge numeral + asymmetric copy
   layout + a visual moodboard tile + a pithy credo. Layouts
   alternate left/right for rhythm.
============================================================ */
const TENETS = [
  {
    n: "01",
    eyebrow: "Customer satisfaction",
    title: "Your success is our success.",
    body: "Clear communication, transparency, and responsiveness through the entire project lifecycle. We measure ourselves on whether you'd ship with us again.",
    credo: "Happy clients. Repeat clients.",
    img: "https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?auto=format&fit=crop&w=1400&q=70",
  },
  {
    n: "02",
    eyebrow: "Results-driven",
    title: "Measurable outcomes, not vanity metrics.",
    body: "Whether it's traffic, conversion rate, or brand visibility — every brief defines what 'won' looks like before we start. Then we go and win it.",
    credo: "If it can't be measured, it doesn't ship.",
    img: "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=1400&q=70",
  },
  {
    n: "03",
    eyebrow: "Collaboration",
    title: "We design with you, not for you.",
    body: "We listen first. Goals, constraints, taste — we soak them up. Strategies are built alongside our clients, not handed to them, so the work feels owned and lasts.",
    credo: "Best ideas win — wherever they come from.",
    img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=70",
  },
  {
    n: "04",
    eyebrow: "Efficiency",
    title: "Done well. Done on time.",
    body: "Streamlined process, agile cadence, the right tools. Projects finish on time and on budget because the system was designed for it — without compromising the work.",
    credo: "Speed without shortcuts.",
    img: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1400&q=70",
  },
  {
    n: "05",
    eyebrow: "Innovation",
    title: "Ahead of the curve, always.",
    body: "We track the platforms, the tools, the patterns — so you don't have to. Cutting-edge isn't the goal; cutting-edge that solves your problem is.",
    credo: "New for new's sake is dead. Useful is forever.",
    img: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&w=1400&q=70",
  },
  {
    n: "06",
    eyebrow: "Expertise",
    title: "Seasoned operators. Specialists, not generalists.",
    body: "From Shopify Plus engineering to performance marketing, every discipline is led by someone who's done it for a decade. Real range, no posturing.",
    credo: "The team you wish you'd hired earlier.",
    img: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1400&q=70",
  },
];

const TenetSpread = ({ tenet, i, total }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Mild image parallax — image floats up as user scrolls past
  const y = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);
  const isOdd = i % 2 === 1;
  const totalLabel = String(total || 6).padStart(2, "0");

  return (
    <div
      ref={ref}
      data-testid={`tenet-${tenet.n}`}
      className="relative bg-[#0B0F14] border-b border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 items-center ${
            isOdd ? "lg:[direction:rtl]" : ""
          }`}
        >
          {/* MEDIA */}
          <div className="lg:col-span-6 [direction:ltr] relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/[0.06]">
            <motion.img
              src={tenet.img}
              alt=""
              style={{ y }}
              className="absolute inset-[-8%] w-[116%] h-[116%] object-cover"
            />
            <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/60 via-transparent to-transparent" />
            {/* Numeric tag floating on image */}
            <span className="absolute top-6 left-6 text-[0.72rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
              {tenet.n} / {totalLabel}
            </span>
            {/* Giant numeral peek-out */}
            <span
              aria-hidden
              className="absolute -bottom-12 -right-4 font-display font-black text-[#F4D64E]/[0.14] leading-[0.78] tracking-[-0.05em] pointer-events-none"
              style={{ fontSize: "clamp(8rem, 18vw, 16rem)" }}
            >
              {tenet.n}
            </span>
          </div>

          {/* COPY */}
          <div className="lg:col-span-6 [direction:ltr]">
            <span data-reveal="eyebrow">
              <Eyebrow>{tenet.eyebrow}</Eyebrow>
            </span>
            <h3
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.5rem] font-black tracking-[-0.035em] leading-[1.02] text-white"
            >
              <span>{tenet.title}</span>
            </h3>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-7 text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.7] max-w-[52ch]"
            >
              <span>{tenet.body}</span>
            </p>
            <div
              data-reveal="eyebrow"
              style={{ "--i": 3 }}
              className="mt-9 inline-flex items-center gap-3 border-t border-[#F4D64E]/30 pt-5"
            >
              <span className="font-display text-[1rem] md:text-[1.15rem] italic font-normal text-[#F4D64E] leading-snug">
                {tenet.credo}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HowWeDoIt = () => {
  const cms = useContent("about");
  const h = cms?.how_we_do_it || {};
  const eyebrow = h.eyebrow || "How we do it";
  const headlineMain = h.headline_main || "Six tenets.";
  const headlineItalic = h.headline_italic || "No";
  const headlineAccent = h.headline_accent || "compromises";
  const description = h.description ||
    "The principles every project runs through — from the first kickoff conversation to the final post-launch review.";
  const tenets = Array.isArray(h.items) && h.items.length
    ? h.items.map((it) => ({
        n: it.tag_number || "",
        eyebrow: it.tag_label || "",
        title: it.title || "",
        body: it.body || "",
        credo: it.credo || "",
        img: it.media || "",
      }))
    : TENETS;
  const total = tenets.length;

  return (
  <section
    id="how"
    data-testid="how-we-do-it"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-12">
      <div className="max-w-4xl">
        <span data-reveal="eyebrow">
          <Eyebrow>{eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2.5rem] md:text-[3.75rem] lg:text-[5rem] font-black tracking-[-0.04em] leading-[0.94] text-white"
        >
          <span>
            {headlineMain}{" "}
            {headlineItalic ? (
              <>
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
              </>
            ) : null}
            <PillarAccent>{headlineAccent}</PillarAccent>.
          </span>
        </h2>
        <p
          data-reveal="mask-block"
          style={{ "--i": 2 }}
          className="mt-9 max-w-[60ch] text-[1.05rem] md:text-[1.15rem] text-neutral-400 leading-[1.65]"
        >
          <span>{description}</span>
        </p>
      </div>
    </div>

    <div className="mt-16">
      {tenets.map((t, i) => (
        <TenetSpread key={i} tenet={t} i={i} total={total} />
      ))}
    </div>
  </section>
  );
};

/* ============================================================
   06 · Closing manifesto pull-quote — the final word
============================================================ */
const Manifesto = ({ onContact }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const cms = useContent("about");
  const m = cms?.manifesto || {};
  const eyebrow = m.eyebrow || "The promise";
  const quotePart1 = m.quote_part_1 || "Choose Sunday for";
  const italic1 = m.italic_1 || "unparalleled";
  const quotePart2 = m.quote_part_2 || "";
  const accentPhrase = m.accent_phrase || "expertise";
  const quotePart3 = m.quote_part_3 || ", innovation, efficiency, collaboration — and an";
  const italic2 = m.italic_2 || "unwavering";
  const quotePart4 = m.quote_part_4 || "commitment to results.";
  const ctaPrimary = m.cta_primary_label || "Start a project";
  const ctaSecondary = m.cta_secondary_label || "See the work";
  const ctaSecondaryHref = m.cta_secondary_href || "/creative";

  return (
    <section
      ref={ref}
      data-testid="manifesto"
      className="relative bg-[#0B0F14] border-t border-white/[0.06] overflow-hidden"
    >
      {/* Big yellow ambient glow on right */}
      <span
        aria-hidden
        className="absolute -top-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#F4D64E]/[0.08] blur-3xl"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-32 md:py-40">
        <div className="max-w-5xl">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="mt-10 font-display text-[2rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4.5rem] font-black tracking-[-0.035em] leading-[1.05] text-white"
          >
            {quotePart1}{" "}
            {italic1 ? (
              <>
                <span className="italic font-normal text-neutral-400">
                  {italic1}
                </span>{" "}
              </>
            ) : null}
            {quotePart2 ? <>{quotePart2}{" "}</> : null}
            {accentPhrase ? <PillarAccent>{accentPhrase}</PillarAccent> : null}
            {quotePart3 ? <>{quotePart3}{" "}</> : null}
            {italic2 ? (
              <>
                <span className="italic font-normal text-neutral-400">{italic2}</span>{" "}
              </>
            ) : null}
            {quotePart4}
          </motion.blockquote>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            className="mt-14 flex flex-wrap items-center gap-5"
          >
            <PrimaryCTA onClick={onContact} testId="manifesto-cta">
              {ctaPrimary}
            </PrimaryCTA>
            {isContactHref(ctaSecondaryHref) ? (
              <button
                type="button"
                onClick={onContact}
                className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] hover:text-white transition-colors"
              >
                {ctaSecondary}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            ) : (
              <Link
                to={ctaSecondaryHref}
                className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] hover:text-white transition-colors"
              >
                {ctaSecondary}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   PAGE
============================================================ */
export default function AboutPage() {
  const { open: openContact } = useContactPanel();

  useSeo({
    route: "/about",
    fallback: {
      title: "Our Story — Sunday Digital",
      description: "Two teams, one studio, one accountable partner.",
    },
  });
  useEffect(() => {
    document.title = "Our Story — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Reveal observer — same as HomeV5 / ShopifyDev / Marketing / Creative
  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="about-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <AboutHero onPrimary={openContact} />
        <Origin />
        <StatsRibbon />
        <TheyTrustUs />
        <TestimonialsV5 contentKey="about" />
        <HowWeDoIt />
        <Manifesto onContact={openContact} />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
